// La relance à J+7 sur la fiche (lot 3 de la refonte). Le mail de relance
// mène ici ; la fiche la propose aussi d'elle-même, une semaine après
// « c'est envoyé ». Trois réponses, comme dans le mail : préparer une relance
// (brouillon rédigé par l'IA, à copier dans sa messagerie), « j'ai eu une
// réponse », « je laisse tomber ». Rien ne part d'ici : Radar n'écrit jamais
// au recruteur à la place de la personne.

import { useState } from 'react'
import { Copy, MailQuestion } from 'lucide-react'
import { get, post } from '../lib/api'
import { useT } from '../lib/i18n'
import { fullDate } from '../lib/format'
import { useData, ActionButton, useToast } from './common'
import { useCandidatureStatus } from '../lib/dictionnaires'

interface Relance {
  sujet: string
  corps: string
  creeLe: string
}

interface EtatRelance {
  statut: string | null
  depuis: string | null
  due: boolean
  relance: Relance | null
}

export type ActionRelance = 'relance' | 'reponse' | 'abandon'

export function BlocRelance({
  candidatureId,
  action,
  onChanged,
}: {
  candidatureId: number
  /** Ce que le lien du mail demandait d'ouvrir. */
  action: ActionRelance | null
  onChanged: () => void
}) {
  const t = useT()
  const toast = useToast()
  const ETAT = useCandidatureStatus()
  const etat = useData(() => get<EtatRelance>(`/api/candidatures/${candidatureId}/relance`), [candidatureId])
  const [relance, setRelance] = useState<Relance | null>(null)
  const [choix, setChoix] = useState<'reponse' | 'abandon' | null>(action === 'reponse' || action === 'abandon' ? action : null)

  const e = etat.data
  // Seulement pour une candidature envoyée et jamais relancée : à J+7, ou
  // plus tôt si un lien du mail l'a demandé.
  if (!e || e.statut !== 'envoye' || (!e.due && !action)) return null
  const brouillon = relance ?? e.relance

  const etape = async (status: string) => {
    await post(`/api/candidatures/${candidatureId}/status`, { status })
    toast(t('candidature.stepToast', { label: ETAT[status].label }))
    onChanged()
    etat.reload()
  }

  return (
    <section className="card relance">
      <div className="relance-h">
        <MailQuestion size={18} />
        <b>{t('relance.titre')}</b>
      </div>
      <p className="field-help">{t('relance.aide', { date: fullDate(e.depuis) })}</p>

      {brouillon ? (
        <div className="relance-brouillon">
          {brouillon.sujet && <div className="relance-objet">{t('relance.objet', { sujet: brouillon.sujet })}</div>}
          <div className="relance-corps">{brouillon.corps}</div>
          <div className="relance-act">
            <ActionButton
              size="sm"
              onClick={async () => {
                await navigator.clipboard.writeText(brouillon.sujet ? `${brouillon.sujet}\n\n${brouillon.corps}` : brouillon.corps)
                toast(t('relance.copie'))
              }}
            >
              <Copy size={13} />{t('relance.copier')}
            </ActionButton>
            <ActionButton size="sm" variant="primary" onClick={() => etape('relance')}>
              {t('relance.envoyee')}
            </ActionButton>
            <ActionButton
              size="sm"
              busyLabel={t('relance.redaction')}
              onClick={async () => setRelance((await post<{ relance: Relance }>(`/api/candidatures/${candidatureId}/relance`, {})).relance)}
            >
              {t('relance.reecrire')}
            </ActionButton>
          </div>
        </div>
      ) : (
        <div className="relance-act">
          <ActionButton
            variant="primary"
            busyLabel={t('relance.redaction')}
            onClick={async () => setRelance((await post<{ relance: Relance }>(`/api/candidatures/${candidatureId}/relance`, {})).relance)}
          >
            {t('relance.preparer')}
          </ActionButton>
        </div>
      )}

      <div className="relance-autres">
        <button type="button" className={`linkbtn${choix === 'reponse' ? ' on' : ''}`} onClick={() => setChoix(choix === 'reponse' ? null : 'reponse')}>
          {t('relance.reponse')}
        </button>
        {' · '}
        <button type="button" className={`linkbtn${choix === 'abandon' ? ' on' : ''}`} onClick={() => setChoix(choix === 'abandon' ? null : 'abandon')}>
          {t('relance.abandon')}
        </button>
      </div>

      {choix === 'reponse' && (
        <div className="relance-choix">
          <span>{t('relance.reponseQuestion')}</span>
          <ActionButton size="sm" onClick={() => etape('entretien')}>{t('relance.entretien')}</ActionButton>
          <ActionButton size="sm" onClick={() => etape('refuse')}>{t('relance.refus')}</ActionButton>
        </div>
      )}
      {choix === 'abandon' && (
        <div className="relance-choix">
          <span>{t('relance.abandonQuestion')}</span>
          <ActionButton
            size="sm"
            onClick={async () => {
              await post(`/api/candidatures/${candidatureId}/archive`, { archived: true })
              toast(t('relance.abandonToast'))
              onChanged()
              etat.reload()
            }}
          >
            {t('relance.abandonOui')}
          </ActionButton>
        </div>
      )}
    </section>
  )
}
